import React, { useState } from 'react';
import { clearSsoAttemptFlag } from '../utils/ssoAuth';
import {
  TextField,
  Button,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

const LoginPage = ({ onLoginSuccess, onRegisterClick }) => {
  const [formData, setFormData] = useState({
    login: '', // Может быть email или телефон
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false); // Управление окном восстановления пароля
  const [resetEmail, setResetEmail] = useState(''); // Email для восстановления
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState(''); // Сообщение после восстановления

  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo =
    location.state?.redirectTo && typeof location.state.redirectTo === 'string'
      ? location.state.redirectTo
      : null;

  // Обработка изменений в форме входа
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Обработка отправки формы входа
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка входа.');
      }

      const data = await response.json();
      localStorage.setItem('token', data.token); // Сохраняем токен в локальном хранилище
      clearSsoAttemptFlag();
      if (onLoginSuccess) onLoginSuccess();
      navigate(redirectTo || '/');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Открытие диалогового окна восстановления пароля
  const handleResetOpen = () => {
    setResetDialogOpen(true);
    setResetMessage('');
    setResetEmail('');
  };

  // Закрытие диалогового окна восстановления пароля
  const handleResetClose = () => {
    setResetDialogOpen(false);
  };

  // Обработка восстановления пароля
  const handleResetSubmit = async () => {
    setResetLoading(true);
    setResetMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка восстановления пароля.');
      }

      setResetMessage('Инструкции по восстановлению пароля отправлены на вашу почту.');
    } catch (err) {
      setResetMessage(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          maxWidth: 400,
          margin: '0 auto',
          padding: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          backgroundColor: '#F5F3F0',
        }}
      >
        <Typography 
          variant="h5" 
          align="center" 
          gutterBottom
          sx={{ 
            color: '#000000', 
            fontWeight: 'bold',
            fontFamily: 'serif',
          }}
        >
          Вход
        </Typography>
        {error && (
          <Typography 
            variant="body2" 
            align="center"
            sx={{ color: '#d32f2f' }}
          >
            {error}
          </Typography>
        )}
        <TextField
          label="Email или телефон"
          name="login"
          type="text"
          value={formData.login}
          onChange={handleChange}
          required
          fullWidth
          placeholder="example@mail.com или +7XXXXXXXXXX"
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#8B7D6B',
              },
              '&:hover fieldset': {
                borderColor: '#6B5D4B',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8B7D6B',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#8B7D6B',
            },
          }}
        />
        <TextField
          label="Пароль"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleChange}
          required
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#8B7D6B',
              },
              '&:hover fieldset': {
                borderColor: '#6B5D4B',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#8B7D6B',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#8B7D6B',
            },
          }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          fullWidth
          sx={{
            backgroundColor: '#8B7D6B',
            color: '#FFFFFF',
            fontWeight: 'bold',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#6B5D4B',
            },
            '&:disabled': {
              backgroundColor: '#8B7D6B',
              opacity: 0.6,
            },
          }}
        >
          {loading ? 'Вход...' : 'Войти'}
        </Button>
        <Typography variant="body2" align="center" sx={{ mt: 2 }}>
          <Button
            variant="text"
            onClick={handleResetOpen}
            sx={{ 
              textTransform: 'none', 
              p: 0,
              color: '#8B7D6B',
              '&:hover': {
                backgroundColor: 'rgba(139, 125, 107, 0.1)',
              },
            }}
          >
            Забыли пароль?
          </Button>
        </Typography>
        <Typography variant="body2" align="center" sx={{ mt: 2, color: '#000000' }}>
          Ещё нет аккаунта?{' '}
          <Button
            variant="text"
            onClick={onRegisterClick}
            sx={{ 
              textTransform: 'none', 
              p: 0,
              color: '#8B7D6B',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: 'rgba(139, 125, 107, 0.1)',
              },
            }}
          >
            Зарегистрироваться
          </Button>
        </Typography>
      </Box>

      {/* Диалоговое окно восстановления пароля */}
      <Dialog 
        open={resetDialogOpen} 
        onClose={handleResetClose}
        PaperProps={{
          sx: {
            backgroundColor: '#F5F3F0',
          },
        }}
      >
        <DialogTitle sx={{ color: '#000000', fontWeight: 'bold', fontFamily: 'serif' }}>
          Восстановление пароля
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#000000' }}>
            Пожалуйста, введите адрес электронной почты, чтобы получить инструкции по восстановлению пароля.
          </DialogContentText>
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            sx={{ 
              mt: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFFFFF',
                '& fieldset': {
                  borderColor: '#8B7D6B',
                },
                '&:hover fieldset': {
                  borderColor: '#6B5D4B',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#8B7D6B',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#8B7D6B',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleResetClose}
            sx={{
              color: '#8B7D6B',
              '&:hover': {
                backgroundColor: 'rgba(139, 125, 107, 0.1)',
              },
            }}
          >
            Отмена
          </Button>
          <Button
            onClick={handleResetSubmit}
            disabled={resetLoading || !resetEmail}
            sx={{
              backgroundColor: '#8B7D6B',
              color: '#FFFFFF',
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#6B5D4B',
              },
              '&:disabled': {
                backgroundColor: '#8B7D6B',
                opacity: 0.6,
              },
            }}
          >
            {resetLoading ? 'Отправка...' : 'Отправить'}
          </Button>
        </DialogActions>
        {resetMessage && (
          <Typography 
            variant="body2" 
            align="center" 
            sx={{ 
              mt: 2, 
              mb: 2,
              color: resetMessage.includes('Ошибка') ? '#d32f2f' : '#8B7D6B',
            }}
          >
            {resetMessage}
          </Typography>
        )}
      </Dialog>
    </>
  );
};

export default LoginPage;
